import { useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { readableText } from "../lib/colors";

// A single "state bubble": collapses all of a state's schools into one glowing
// architectural marker whose height grows with the number of institutions.
// Shown only when the camera is zoomed out; clicking it flies in to reveal the
// individual towers.
export function StateCluster({ cluster, onSelect, onHover }) {
  const { state, count, x, z, color } = cluster;
  const capRef = useRef();
  const [hovered, setHovered] = useState(false);

  const h = 0.9 + Math.min(count, 12) * 0.45;
  const r = 0.55 + Math.min(count, 12) * 0.05;

  useFrame((s) => {
    if (!capRef.current) return;
    const pulse = hovered ? 1.2 : 0.7 + Math.sin(s.clock.elapsedTime * 2 + x) * 0.12;
    capRef.current.material.emissiveIntensity = pulse;
  });

  return (
    <group
      position={[x, 0, z]}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
        onHover && onHover(cluster);
        document.body.style.cursor = "pointer";
      }}
      onPointerOut={() => {
        setHovered(false);
        onHover && onHover(null);
        document.body.style.cursor = "auto";
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect && onSelect(cluster);
      }}
    >
      {/* Pedestal */}
      <mesh position={[0, 0.05, 0]} castShadow>
        <cylinderGeometry args={[r * 1.15, r * 1.3, 0.1, 22]} />
        <meshStandardMaterial color="#DADFE6" roughness={0.6} metalness={0.1} />
      </mesh>
      {/* Stacked pillar (height encodes count) */}
      <mesh position={[0, h / 2 + 0.1, 0]} castShadow>
        <cylinderGeometry args={[r, r * 1.08, h, 22]} />
        <meshStandardMaterial
          color="#F2F4F7"
          emissive={color}
          emissiveIntensity={hovered ? 0.5 : 0.28}
          roughness={0.35}
          metalness={0.35}
        />
      </mesh>
      {/* Glowing cap */}
      <mesh ref={capRef} position={[0, h + 0.1 + r * 0.7, 0]}>
        <sphereGeometry args={[r * 0.82, 18, 14]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={0.8}
          roughness={0.2}
          metalness={0.6}
        />
      </mesh>
      {/* Count badge */}
      <Html
        position={[0, h + 0.1 + r * 1.7, 0]}
        center
        distanceFactor={18}
        zIndexRange={[30, 0]}
      >
        <div
          className="cluster-badge"
          data-testid={`cluster-${state}`}
          style={{ background: color, color: readableText(color) }}
          onMouseEnter={() => {
            setHovered(true);
            onHover && onHover(cluster);
          }}
          onMouseLeave={() => {
            setHovered(false);
            onHover && onHover(null);
          }}
          onClick={(e) => {
            e.stopPropagation();
            onSelect && onSelect(cluster);
          }}
        >
          <span className="cluster-state">{state}</span>
          <span className="cluster-count">{count}</span>
        </div>
      </Html>
    </group>
  );
}
